import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, ExternalLink, Copy } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const field = "w-full bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none";
const empty = { category: "adoption", title: "", description: "", source_name: "", source_date: "", source_url: "", image_url: "", order: 100, published: true };

export default function DataHubAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/data-items").then((r) => { setItems(r.data.items || []); setCats(r.data.categories || []); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const catLabel = (k) => cats.find((c) => c.key === k)?.label || k;

  // Lightweight "content health" tally (admin-added items only, built-ins excluded)
  const mine = items.filter((it) => !it.builtin);
  const countByCat = cats.map((c) => ({ ...c, n: mine.filter((it) => it.category === c.key).length }));
  const filledCats = countByCat.filter((c) => c.n > 0).length;
  const thinCats = countByCat.filter((c) => c.n === 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image_url) { toast.error("Title and a chart image are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/data-items", { ...form, type: "image", order: Number(form.order) || 100 });
      toast.success("Data item added");
      setForm(empty); load();
    } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const togglePublish = async (it) => {
    await api.patch(`/admin/data-items/${it.id}`, { published: !it.published }).catch(() => {});
    load();
  };
  const remove = async (it) => {
    if (it.builtin) { toast.error("Built-in items can't be deleted — unpublish instead"); return; }
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    await api.delete(`/admin/data-items/${it.id}`).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
    load();
  };
  // Prefill the form with an item's category/source so a related stat is fast to add.
  const duplicate = (it) => {
    setForm({
      category: it.category || "adoption",
      title: "", description: "", image_url: "",
      source_name: it.source_name || "", source_date: it.source_date || "",
      source_url: it.source_url || "", order: it.order || 100, published: true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Source & category copied — add the new stat's title and image");
  };

  return (
    <div className="space-y-6">
      {/* Content health tally */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="datahub-content-health">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-display font-bold text-lg" data-testid="datahub-health-summary">{mine.length}</span>
          <span className="text-sm text-[#94A3B8]">stat{mine.length === 1 ? "" : "s"} across {filledCats} of {cats.length} categories</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {countByCat.map((c) => (
            <span key={c.key}
              className={`text-xs font-mono-tech px-2.5 py-1 rounded-full border ${c.n === 0 ? "border-[#F59E0B]/40 text-[#F59E0B] bg-[#F59E0B]/5" : "border-white/10 text-[#CBD5E1]"}`}>
              {c.label}: {c.n}
            </span>
          ))}
        </div>
        {thinCats.length > 0 && (
          <p className="mt-3 text-xs text-[#F59E0B]" data-testid="datahub-thin-hint">
            {thinCats.length === 1 ? "This category has" : "These categories have"} no data yet: {thinCats.map((c) => c.label).join(", ")}.
          </p>
        )}
      </div>

      <div data-testid="admin-datahub" className="grid lg:grid-cols-2 gap-8">
      {/* Add form */}
      <div>
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Plus className="w-4 h-4" /> Add a data item</h3>
        <p className="text-sm text-[#94A3B8] mb-4">Upload a chart image and cite its source. It appears instantly in the chosen category on the public hub.</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs text-[#94A3B8] mb-1">Category</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className={field} data-testid="dh-category">
              {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Title *" className={field} data-testid="dh-title" />
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Description — line breaks are preserved, and any https:// links become clickable" rows={5} className={field} data-testid="dh-description" />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.source_name} onChange={(e) => set("source_name", e.target.value)} placeholder="Source name" className={field} data-testid="dh-source-name" />
            <input value={form.source_date} onChange={(e) => set("source_date", e.target.value)} placeholder="Source date (e.g. 2025)" className={field} data-testid="dh-source-date" />
          </div>
          <input value={form.source_url} onChange={(e) => set("source_url", e.target.value)} placeholder="Source URL (https://…)" className={field} data-testid="dh-source-url" />
          <ImageUpload label="Upload chart image *" onUploaded={(url) => set("image_url", url)} testid="dh-image" />
          {form.image_url && <img src={form.image_url} alt="preview" className="rounded-md max-h-40 border border-white/10" />}
          <div className="flex items-center gap-3">
            <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} placeholder="Order" className={`${field} w-28`} data-testid="dh-order" />
            <label className="flex items-center gap-2 text-sm text-[#94A3B8]"><input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} data-testid="dh-published" /> Published</label>
          </div>
          <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md hover:brightness-110 disabled:opacity-50" data-testid="dh-submit">
            {busy ? "Adding…" : "Add data item"}
          </button>
        </form>
      </div>

      {/* Existing items */}
      <div>
        <h3 className="font-display font-bold text-lg mb-4">Existing items ({items.length})</h3>
        <div className="space-y-2" data-testid="dh-list">
          {items.map((it) => (
            <div key={it.id} className="card-surface rounded-lg p-3 flex items-center gap-3" data-testid={`dh-row-${it.id}`}>
              {it.image_url
                ? <img src={it.image_url} alt="" className="w-14 h-14 object-cover rounded shrink-0" />
                : <div className="w-14 h-14 rounded bg-[#0e1424] flex items-center justify-center text-[10px] text-[#64748B] shrink-0">EMBED</div>}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{it.title} {it.builtin && <span className="text-[10px] text-[#64748B]">(built-in)</span>}</div>
                <div className="text-xs text-[#64748B]">{catLabel(it.category)} · {it.source_name || "no source"}</div>
              </div>
              <button onClick={() => duplicate(it)} title="Duplicate source & category" data-testid={`dh-duplicate-${it.id}`}
                className="p-2 rounded text-[#00F0FF] hover:bg-[#00F0FF]/10"><Copy className="w-4 h-4" /></button>
              <button onClick={() => togglePublish(it)} title={it.published ? "Published" : "Hidden"} data-testid={`dh-toggle-${it.id}`}
                className={`p-2 rounded ${it.published ? "text-[#4ade80]" : "text-[#64748B]"} hover:bg-white/5`}>
                {it.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              {!it.builtin && (
                <button onClick={() => remove(it)} data-testid={`dh-delete-${it.id}`} className="p-2 rounded text-[#f87171] hover:bg-[#f87171]/10"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
    </div>
  );
}
